import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { DominosRootStore } from './shared/global/store/index';
import { initializeModules } from './shared/global/importer';
import testABdl from './shared/utils/storage/testABdl';
import { insiderHomePageEvent } from './shared/utils/insiderHomePageEvent';

import { goFish } from './shared/global/api';
import { APP_CONFIG } from './shared/global/base';
import { getConfig } from './modules/AppMain/config';

// Setup store instance for all modules (not only app)
export const dominosStore = new DominosRootStore();

testABdl();
// homepage_view
insiderHomePageEvent();

(function(){
    // Loading dynamic modules (gathered from DOM)
    window.addEventListener('DOMContentLoaded', () => {
        initializeModules(document);
        if (window.Insider) {
            window.Insider.systemRules.getLang = function() {
                return (document.documentElement.lang || 'en_US');
            }; 
        }
    });
})();

declare global {
    interface Window {
        /**
         * Adds product to cart by type:
         * - 98: addproduct (simple product)
         * - 2: promotion flow (useupsellpromotion + redirect)
         * - 1 / 99: redirect to configurator / creator (no silent add)
         *
         * Optional localId + deliveryType (0 = Pickup, 1 = Delivery) set the store first.
         */
        AddToCart: (
            productId: string,
            quantity?: number,
            localId?: string,
            deliveryType?: 0 | 1
        ) => Promise<string>;
    }
}

window.AddToCart = async (
    productId: string,
    quantity: number = 1,
    localId?: string,
    deliveryType: 0 | 1 = 1
) => {
    if (localId) {
        const localResponse = await dominosStore.localStore.setCurrentLocal(localId, deliveryType);
        if (!localResponse.Status) {
            throw new Error(localResponse.Message || 'setcurrentlocal failed');
        }
    }

    const localguid = dominosStore.localStore.localId;
    if (!localguid) {
        throw new Error('Local not selected');
    }

    const response = await goFish({
        api: 'getproductlist',
        data: { localguid }
    }) as ProductsList;

    if (!response.Status || !response.Product) {
        throw new Error(response.Message || 'getproductlist failed');
    }

    const startPromotionFlow = async (promotionId: string) => {
        const ok = await dominosStore.cartStore.checkUpsellPromotion(promotionId);
        if (!ok) {
            throw new Error('Promotion cannot be used (useupsellpromotion failed)');
        }

        sessionStorage.setItem(`upsell_source_${promotionId}`, '1');

        const details = await goFish({
            api: 'getpromotiondetails',
            data: { localguid, productguid: promotionId }
        }) as PromotionProductsList;

        const step = details.Status && details.Group ? details.Group[0] : null;
        const isInstantPromo =
            details.Status &&
            details.Group?.length === 1 &&
            step?.Product?.length === 1 &&
            step.RequiredQuantity === 1 &&
            step.Product[0].Type === 98;

        // Model I: 1 group, 1 simple product, no choice — addcoupon without redirect
        if (isInstantPromo && step) {
            const coupon: CartCouponProduct = {
                Id: promotionId,
                Count: quantity,
                OldCartId: undefined,
                CartCoupons: [
                    {
                        Id: step.Guid,
                        CartPizzas: [],
                        CartProducts: [
                            {
                                Id: step.Product[0].Id,
                                Count: 1,
                                OldCartId: undefined,
                                AnalyticsItemListName: undefined
                            }
                        ]
                    }
                ]
            };

            const errorMessage = await dominosStore.cartStore.addPromotionToCart(
                details.Name || '',
                coupon,
                []
            );

            if (errorMessage) {
                throw new Error(errorMessage);
            }

            return 'added_instant_promotion';
        }

        localStorage.setItem('isBackToCard', 'true');
        window.location.href = `${APP_CONFIG.navigation.menu}/${promotionId}/1`;
        return 'redirected_to_promotion';
    };

    const product = response.Product.find(p => p.Id === productId);

    // Hidden upsell promotions (Visible: false) are not in getproductlist
    if (!product) {
        return startPromotionFlow(productId);
    }

    const { routes } = getConfig();

    // Promotion
    if (product.Type === 2) {
        return startPromotionFlow(product.Id);
    }

    // Pizza configurator – cannot be added with only id + quantity
    if (product.Type === 1) {
        dominosStore.cartStore.releasePromotionMode();
        window.location.href = `${routes.configurator}/${product.Id}`;
        return 'redirected_to_configurator';
    }

    // Creator
    if (product.Type === 99) {
        dominosStore.cartStore.releasePromotionMode();
        window.location.href = `${routes.creator}/${product.Id}`;
        return 'redirected_to_creator';
    }

    // Simple product (Type 98 and fallback)
    return dominosStore.cartStore.addToCart(
        productId,
        quantity,
        product.Price,
        product.Name,
        ''
    );
};

//  przykład użycia
// await window.AddToCart(productId, 1, localGuid, 1); // dostawa
// await window.AddToCart(productId, 1, localGuid, 0); // odbiór
// await window.AddToCart(productId, 1);               // bez zmiany lokalu
